import { DestroyRef, inject, Injectable } from "@angular/core";
import { RealEstateStoreEventsService } from "./real-estate-store-events.service";
import { RealEstateStoreService } from "./real-estate-store.service";
import { CompareObjectsService } from "../../compare-objects/compare-objects.service";
import { ThematicInfoService } from "@services/real-estate/thematic-info/thematic-info.service";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { combineLatest, filter, map, tap } from "rxjs";
import { MapCompareableLayerService } from "@services/map-compareable-layer.service";
import { CompareType } from "src/app/shared/contract/Enum/CompareObject";
import { RENTAL_COMPARE_TYPES, PURCHASE_COMPARE_TYPES } from "@helpers/compareable-helper";
import { PriceInformation } from "src/app/shared/contract/AVM/PriceInformation";
import { Address } from "src/app/shared/contract/Address/Address";
import { CompareableMarkerService } from "@services/compareable-marker.service";
import { PerimeterMarkerService } from "@services/perimeter-marker.service";
import { MapViewService } from "@services/map-view.service";
import { MapContainerService } from "@services/map-container.service";
import { AccessibilityService } from "@components/accessibility/accessibility.service";
import { AccessibilityStore } from "@components/accessibility/accessibility-store.service";

@Injectable({
  providedIn: "root"
})
export class RealEstateMapService {
  private readonly realEstateStoreEventsService = inject(RealEstateStoreEventsService);
  private readonly realEstateStoreService = inject(RealEstateStoreService);
  private readonly compareObjectsService = inject(CompareObjectsService);
  private readonly thematicInfoService = inject(ThematicInfoService);
  private readonly mapViewService = inject(MapViewService);
  private readonly mapContainerService = inject(MapContainerService);
  private readonly mapCompareableLayerService = inject(MapCompareableLayerService);
  private readonly compareableMarkerService = inject(CompareableMarkerService);
  private readonly perimeterMarkerService = inject(PerimeterMarkerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly accessibilityStoreService = inject(AccessibilityStore);

  private readonly realEstate$ = toObservable(this.realEstateStoreService.realEstateSignal);
  private readonly thematicTab$ = toObservable(this.thematicInfoService.selectedThematicTab);
  private readonly isRent$ = toObservable(this.thematicInfoService.isRent);
  private readonly selectedCompareType$ = toObservable(this.compareObjectsService.selectedCompareType);
  private readonly priceInformation$ = toObservable(this.realEstateStoreService.priceInformation);
  readonly selectedCompareObject$ = toObservable(this.compareObjectsService.selectedCompareObject);
  private readonly accessibilityData$ = toObservable(this.accessibilityStoreService.accessibilityData);

  readonly compareLayerContext = combineLatest([
    this.realEstateStoreEventsService.valueFixingChange$,
    this.realEstateStoreEventsService.addressChange$,
    this.selectedCompareType$,
    this.priceInformation$,
    this.thematicTab$
  ]).pipe(
    filter(([, , , , thematicTab]) => thematicTab?.Name !== "Accessibility"),
    map(([valueFixingChange, addressChange, selectedCompareType, priceInformation, thematicTab]) => ({
      valueFixingChange,
      addressChange,
      selectedCompareType,
      priceInformation,
      thematicTab
    }))
  );

  constructor() {
    this.handleRentMode();
    this.handlePurchaseMode();
    this.handleThematicTab();
    this.handleSelectedCompareObject();
    this.handleRealEstateReset();
    this.handlePerimeter();
    this.handleCompareables();
    this.handleAccessibility();
  }

  private handleSelectedCompareObject() {
    this.selectedCompareObject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(selectedCompareObject => {
      if (selectedCompareObject) {
        this.mapCompareableLayerService.resetSelectedCompareObjects();
        this.mapCompareableLayerService.selectCompareObject(selectedCompareObject, this.thematicInfoService.isRent());
      } else {
        this.mapCompareableLayerService.resetSelectedCompareObjects();
      }
    });
  }

  private handleRealEstateReset() {
    this.realEstate$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(realEstate => !realEstate),
        tap(() => {
          this.perimeterMarkerService.removePerimeterMarker();
          this.mapCompareableLayerService.removeAllLayers();
        })
      )
      .subscribe();
  }

  private handlePerimeter() {
    combineLatest([this.realEstateStoreEventsService.addressChange$, this.priceInformation$, this.isRent$, this.thematicTab$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([address, priceInformation]) => !!address && !!priceInformation),
        tap(([address, priceInformation, isRent, thematicTab]) => {
          if (thematicTab?.Name === "Accessibility") {
            this.perimeterMarkerService.removePerimeterMarker();
          } else {
            this.perimeterMarkerService.setPerimeterMarker(address, priceInformation, isRent);
          }
        }),
        tap(([address, , , thematicTab]) => {
          if (thematicTab?.Name === "Accessibility") {
            return;
          }
          const componentWidth = this.mapContainerService.rightComponentConfig().currentWidth();
          this.mapViewService.centerViewToAddressOrCompareables(address, false, componentWidth);
        })
      )
      .subscribe();
  }

  private handleCompareables() {
    this.compareLayerContext.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(mapContext => {
      if (
        mapContext.valueFixingChange != null &&
        mapContext.addressChange != null &&
        mapContext.selectedCompareType != null &&
        mapContext.priceInformation != null
      ) {
        this.drawCompareableLayer(mapContext.priceInformation, mapContext.addressChange, mapContext.selectedCompareType);
      }
    });
  }

  private handleThematicTab(): void {
    this.thematicTab$
      .pipe(
        filter(thematicTab => !!thematicTab),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(thematicTab => {
        if (thematicTab?.Name === "Accessibility") {
          this.mapCompareableLayerService.removePurchaseLayer();
          this.mapCompareableLayerService.removeRentLayer();
          this.accessibilityService.hideKpiLayer();
        } else {
          this.accessibilityService.showKpiLayer();
        }
      });
  }

  private handleAccessibility(): void {
    combineLatest([this.realEstate$, this.accessibilityData$, this.thematicTab$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          ([realEstate, accessibilityData, thematicTab]) => !!realEstate && !!accessibilityData && thematicTab?.Name === "Accessibility"
        ),
        tap(([realEstate]) => {
          const panelWidth = this.mapContainerService.rightComponentConfig().currentWidth();
          this.accessibilityService.init(realEstate.Address.Coordinate, panelWidth);
          this.accessibilityService.initAccessibility();
        })
      )
      .subscribe();
  }

  private handleRentMode(): void {
    this.isRent$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(isRent => isRent)
      )
      .subscribe(() => this.mapCompareableLayerService.removePurchaseLayer());
  }

  private handlePurchaseMode(): void {
    this.isRent$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(isRent => !isRent)
      )
      .subscribe(() => this.mapCompareableLayerService.removeRentLayer());
  }

  private drawCompareableLayer(priceInformation: PriceInformation, address: Address, selectedCompareType: CompareType) {
    if (!priceInformation || Object.keys(priceInformation).length === 0 || !address) {
      return;
    }

    const isRent = this.thematicInfoService.isRent();
    let compareTypes: CompareType[] = [];
    if (isRent) {
      compareTypes = RENTAL_COMPARE_TYPES;
    } else {
      compareTypes = PURCHASE_COMPARE_TYPES;
    }

    const filteredCompareTypes = compareTypes.filter(type => type !== selectedCompareType);

    for (const type of filteredCompareTypes) {
      this.compareableMarkerService.setCompareableMarkerForCompareType(priceInformation, address, isRent, type);
    }

    this.compareableMarkerService.setCompareableMarkerForCompareType(priceInformation, address, isRent, selectedCompareType);
  }
}
